import { useState, useEffect, useCallback } from 'react'
import GenericTable from '../GenericTable'
import { d1Query, d1Insert, d1Update, d1Delete } from '../../services/d1Service'
import type { GalleryItem, StatusMessage } from '../../types'

interface Props { eiin: string; onStatus: (msg: StatusMessage) => void }

export default function GalleryTab({ eiin, onStatus }: Props) {
  const [items, setItems] = useState<GalleryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Partial<GalleryItem> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await d1Query<GalleryItem>(`SELECT * FROM gallery WHERE institution_eiin = ? ORDER BY id DESC`, [eiin])) }
    catch (e: any) { onStatus({ type: 'error', text: e.message }) }
    finally { setLoading(false) }
  }, [eiin])

  useEffect(() => { load() }, [load])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editing) return
    try {
      const rec = { ...editing, institution_eiin: eiin }
      if ((editing as any).id) await d1Update('gallery', (editing as any).id, rec)
      else await d1Insert('gallery', rec)
      onStatus({ type: 'success', text: 'Gallery item saved!' })
      setEditing(null); load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  const toggleActive = async (row: GalleryItem) => {
    try {
      const newStatus = row.is_active === 0 ? 1 : 0
      await d1Update('gallery', row.id!, { is_active: newStatus })
      onStatus({ type: 'success', text: `Gallery item ${newStatus ? 'activated' : 'deactivated'}!` })
      load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  return (
    <GenericTable
      title="Gallery" icon="🖼️"
      items={items} loading={loading} editingItem={editing}
      columns={[
        { key: 'type', label: 'Type', width: '70px', render: (v) => v === 'photo' ? '📷 Photo' : '🎬 Video' },
        { key: 'title_bn', label: 'Title (BN)', width: '180px' },
        { key: 'title_en', label: 'Title (EN)', width: '180px' },
        { key: 'url', label: 'URL', width: '180px', render: (v) => <a href={v} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: '12px' }}>🔗 link</a> },
        {
          key: 'is_active', label: 'Status', width: '100px',
          render: (v, row) => (
            <button
              onClick={(e) => { e.stopPropagation(); toggleActive(row) }}
              style={{
                padding: '3px 10px', borderRadius: '12px', border: 'none', fontWeight: 700, fontSize: '11px', cursor: 'pointer',
                background: v === 0 ? '#fee2e2' : '#dcfce7',
                color: v === 0 ? '#dc2626' : '#16a34a'
              }}
            >
              {v === 0 ? '🔴 Deactive' : '🟢 Active'}
            </button>
          )
        }
      ]}
      formFields={[
        { key: 'type', label: 'Type', type: 'select', options: [
          { value: 'photo', label: '📷 Photo' }
        ]},
        { key: 'title_bn', label: 'Title (BN)', required: true },
        { key: 'title_en', label: 'Title (EN)', required: true },
        { key: 'url', label: 'Photo Image', type: 'image', folder: 'gallery', required: true },
        { key: 'thumbnail_url', label: 'Thumbnail', type: 'image', folder: 'gallery/thumbnails' },
        { key: 'is_active', label: 'Status', type: 'select', options: [
          { value: '1', label: '🟢 Active' },
          { value: '0', label: '🔴 Deactive' }
        ]}
      ]}
      onAdd={() => setEditing({ type: 'photo', title_bn: '', title_en: '', url: '', thumbnail_url: '', is_active: 1 })}
      onEdit={(item) => setEditing({ ...item })}
      onDelete={async (id) => { try { await d1Delete('gallery', id); onStatus({ type: 'success', text: 'Deleted!' }); load() } catch (e: any) { onStatus({ type: 'error', text: e.message }) } }}
      onSave={handleSave}
      onCancel={() => setEditing(null)}
      onFieldChange={(key, val) => setEditing(prev => prev ? { ...prev, [key]: val } : prev)}
    />
  )
}
